/**
 * Swap main fingering and alternative fingerings for PDF fingering groups
 * This maintains the order: 1st main <-> 1st alt, 2nd main <-> 2nd alt, etc.
 */

interface FingeringNumber {
  mainFingering: number;
  altFingering: number;
  // Stable cross-system id matching the MusicXML `<note id="…">`.
  // The swap below only touches main/alt and leaves this untouched —
  // noteheadId stays pinned to its notehead regardless of swap direction.
  noteheadId?: string;
}

interface FingeringGroup {
  fingeringNumList: FingeringNumber[];
  coordinate: {
    x: number;
    y: number;
    upward: boolean;
  };
  noteheadIdList?: string[];
}

// Mirror of `NoteheadData` in `usePdfStateQueue.ts`. Duplicated here (rather
// than imported) to keep this util's type set self-contained — these
// definitions deliberately mirror the canonical hook types.
interface NoteheadData {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  pitchStep: number;
  alter: number;
  noteheadId: string;
}

interface ScoreSystemData {
  imageBlobPath: string;
  fingeringGroupList: FingeringGroup[];
  noteheadDataList?: NoteheadData[];
}

/**
 * Swap main and alt fingerings for a single fingering group
 * @param fingeringGroup - The fingering group to swap
 */
export const swapFingeringGroup = (fingeringGroup: FingeringGroup): void => {
  fingeringGroup.fingeringNumList.forEach((fingeringNum) => {
    // Swap mainFingering and altFingering
    const temp = fingeringNum.mainFingering;
    fingeringNum.mainFingering = fingeringNum.altFingering;
    fingeringNum.altFingering = temp;
  });
};

/**
 * Swap main and alt fingerings for multiple groups
 * @param scoreSystems - The array of score system data
 * @param groupsToSwap - Array of {imageIndex, groupIndex} to swap
 * @returns A new deep copy with swapped fingerings
 */
export const swapPdfFingeringData = (
  scoreSystems: ScoreSystemData[],
  groupsToSwap: Array<{ imageIndex: number; groupIndex: number }>
): ScoreSystemData[] => {
  // Create deep copy of current data
  const newFingering = JSON.parse(
    JSON.stringify(scoreSystems)
  ) as ScoreSystemData[];

  // Swap main and alt fingerings for each group
  groupsToSwap.forEach((group) => {
    const fingeringGroup =
      newFingering[group.imageIndex].fingeringGroupList[group.groupIndex];
    swapFingeringGroup(fingeringGroup);
  });

  return newFingering;
};

export type { ScoreSystemData, FingeringGroup, FingeringNumber, NoteheadData };
